(() => {
  if (customElements.get('personalised-shelf-mock')) return;
  const LIVE_SHELF_TIMEOUT_MS = 30000;

  class PersonalisedShelfMock extends HTMLElement {
    get minimumProducts() {
      return Math.max(1, Number.parseInt(this.dataset.minimumProducts, 10) || 1);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.scheduleCounterUpdate = this.scheduleCounterUpdate.bind(this);
      this.requests = new Set();
      this.trackedImpressions = new Set();

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.bindSliders();
      window.addEventListener('resize', this.scheduleCounterUpdate, { passive: true });
      this.scheduleCounterUpdate();
      this.loadLiveShelf();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.unbindSliders();
      window.removeEventListener('resize', this.scheduleCounterUpdate);
      this.requests?.forEach((controller) => controller.abort());
      cancelAnimationFrame(this.counterFrame);
      this.initialized = false;
    }

    bindSliders() {
      this.unbindSliders();
      this.sliders = [...this.querySelectorAll('.scroll-slider__slider')];
      this.sliders.forEach((slider) => slider.addEventListener('scroll', this.scheduleCounterUpdate, { passive: true }));
    }

    unbindSliders() {
      this.sliders?.forEach((slider) => slider.removeEventListener('scroll', this.scheduleCounterUpdate));
      this.sliders = [];
    }

    async loadLiveShelf() {
      const endpoint = this.dataset.shelfEndpoint;
      if (!endpoint) return;
      this.setLiveStatus('loading');

      try {
        const response = await this.requestShelf();
        if (response.status === 204) {
          this.setLiveStatus('empty');
          this.trackImpression('discover');
          return;
        }
        if (!response.ok) {
          this.setLiveStatus('error');
          return;
        }

        const html = await response.text();
        const document = new DOMParser().parseFromString(html, 'text/html');
        const liveContents = document.querySelectorAll('[data-live-shelf-content]');
        const liveContent = liveContents.length === 1 ? liveContents[0] : null;
        const livePanels = liveContent?.querySelectorAll('[data-live-shelf-panel]') || [];
        const livePanel = livePanels.length === 1 ? livePanels[0] : null;
        if (!liveContent || !livePanel || liveContent.querySelector('personalised-shelf-mock')) {
          this.setLiveStatus('invalid');
          console.warn('[Thryft shelf] Shopify returned an invalid recommendation fragment.', {
            contentType: response.headers.get('content-type'),
            liveContentCount: liveContents.length,
            livePanelCount: livePanels.length,
            cardCount: document.querySelectorAll('[data-shelf-card]').length,
            hasNestedShelf: Boolean(liveContent?.querySelector('personalised-shelf-mock')),
            hasUnrenderedLiquid: html.includes('{%') || html.includes('{{'),
          });
          return;
        }
        if (livePanel.querySelectorAll('[data-shelf-card]').length < this.minimumProducts) {
          this.setLiveStatus('empty');
          return;
        }

        const currentContent = this.querySelector('[data-shelf-content]');
        if (!currentContent) {
          this.setLiveStatus('invalid');
          return;
        }
        this.unbindSliders();
        currentContent.replaceWith(liveContent);
        this.dataset.defaultFamily = liveContent.dataset.defaultFamily;
        this.dataset.personalised = 'true';
        this.setLiveStatus('loaded');
        this.setText('.personalised-shelf__preheading', this.dataset.livePreheading);
        this.setText('.personalised-shelf__heading', this.dataset.liveHeading);
        this.setText('.personalised-shelf__subheading', this.dataset.liveSubheading);
        this.bindSliders();
        this.scheduleCounterUpdate();
        this.trackImpression(this.dataset.defaultFamily);
      } catch (error) {
        // The generic shelf is already visible and remains the safe fallback.
        if (this.isConnected) {
          const status = error.name === 'AbortError' ? 'timeout' : 'error';
          this.setLiveStatus(status);
          console.warn(`[Thryft shelf] Live recommendations ${status}.`, { name: error.name });
        }
      }
    }

    async requestShelf(family = '') {
      const url = new URL(this.dataset.shelfEndpoint, window.location.origin);
      if (url.origin !== window.location.origin) throw new TypeError('Invalid shelf endpoint');
      // `section_id` is reserved by Shopify's Section Rendering API.
      url.searchParams.set('shelf_section_id', this.dataset.sectionId);
      if (family) url.searchParams.set('family', family);
      const controller = new AbortController();
      this.requests.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), LIVE_SHELF_TIMEOUT_MS);
      try {
        return await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeout);
        this.requests.delete(controller);
      }
    }

    async loadFamily(family) {
      const placeholder = this.querySelector(`[data-shelf-panel-placeholder][data-family="${family}"]`);
      if (!placeholder || placeholder.dataset.loading === 'true') return;
      placeholder.dataset.loading = 'true';
      try {
        const response = await this.requestShelf(family);
        if (!response.ok) return;
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const panels = document.querySelectorAll(`[data-live-shelf-panel][data-family="${family}"]`);
        const panel = panels.length === 1 ? panels[0] : null;
        if (
          !panel
          || document.querySelector('personalised-shelf-mock')
          || panel.querySelectorAll('[data-shelf-card]').length < this.minimumProducts
        ) return;
        this.unbindSliders();
        placeholder.replaceWith(panel);
        this.bindSliders();
        this.activateFamily(family);
      } catch {
        // Keep the current family visible if the optional panel cannot load.
      } finally {
        if (placeholder.isConnected) placeholder.dataset.loading = 'false';
      }
    }

    setLiveStatus(status) {
      this.dataset.liveStatus = status;
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element && value) element.textContent = value;
    }

    handleClick(event) {
      const tab = event.target.closest('[data-shelf-tab]');
      if (tab) {
        this.activateFamily(tab.dataset.shelfTab);
        return;
      }

      const moreButton = event.target.closest('[data-shelf-more]');
      if (moreButton) {
        this.toggleMore(moreButton);
        return;
      }

      const lessButton = event.target.closest('[data-shelf-less]');
      if (lessButton) {
        this.dismissCard(lessButton.closest('[data-shelf-card]'));
        return;
      }

      const card = event.target.closest('[data-shelf-card]');
      if (!card || event.target.closest('[data-shelf-more], [data-shelf-less]')) return;
      if (event.target.closest('.product-item__quick-add')) {
        this.sendFeedback('add_to_cart', card);
      } else if (event.target.closest('a[href]')) {
        this.sendFeedback('click', card);
      }
    }

    handleKeydown(event) {
      const activeTab = event.target.closest('[data-shelf-tab]');
      if (!activeTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const tabs = [...this.querySelectorAll('[data-shelf-tab]')];
      const currentIndex = tabs.indexOf(activeTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      event.preventDefault();
      tabs[nextIndex].focus();
      this.activateFamily(tabs[nextIndex].dataset.shelfTab);
    }

    activateFamily(family) {
      if (this.querySelector(`[data-shelf-panel-placeholder][data-family="${family}"]`)) {
        this.loadFamily(family);
        return;
      }
      this.querySelectorAll('[data-shelf-tab]').forEach((tab) => {
        const selected = tab.dataset.shelfTab === family;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      this.querySelectorAll('[data-shelf-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.family !== family;
      });

      this.scheduleCounterUpdate();
      this.trackImpression(family);
    }

    toggleMore(button) {
      if (button.getAttribute('aria-pressed') === 'true') return;
      button.setAttribute('aria-pressed', 'true');
      button.classList.add('is-selected');

      const card = button.closest('[data-shelf-card]');
      const status = card.closest('[data-shelf-panel]')?.querySelector('[data-shelf-status]');
      if (!status) return;

      status.replaceChildren(document.createTextNode(
        `Thanks — we’ll show you more picks like ${card.dataset.productTitle}.`
      ));
      status.hidden = false;
      this.sendFeedback('more', card);
    }

    dismissCard(card) {
      if (!card) return;

      const panel = card.closest('[data-shelf-panel]');
      const replacement = panel?.querySelector('[data-shelf-card][data-shelf-reserve][hidden]');
      const status = panel?.querySelector('[data-shelf-status]');
      if (!panel || !status) return;

      card.hidden = true;
      if (replacement) replacement.hidden = false;
      status.textContent = `${card.dataset.productTitle} hidden. We’ll use that to improve your shelf.`;
      status.hidden = false;
      this.sendFeedback('less', card);
      this.scheduleCounterUpdate();
      replacement?.querySelector('[data-shelf-less]')?.focus();
    }

    trackImpression(family) {
      if (!family || this.trackedImpressions.has(family)) return;
      this.trackedImpressions.add(family);
      const card = this.querySelector(`[data-shelf-panel][data-family="${family}"] [data-shelf-card]`);
      this.sendFeedback('impression', card, family);
    }

    sendFeedback(action, card, family = card?.dataset.family) {
      const endpoint = this.dataset.feedbackEndpoint;
      if (!endpoint || !family || !crypto.randomUUID) return;
      let url;
      try {
        url = new URL(endpoint, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          eventId: crypto.randomUUID(),
          family,
          productId: card?.dataset.productId || '',
        }),
      }).catch(() => {});
    }

    scheduleCounterUpdate() {
      cancelAnimationFrame(this.counterFrame);
      this.counterFrame = requestAnimationFrame(() => this.updateCounter());
    }

    updateCounter() {
      const panel = [...this.querySelectorAll('[data-shelf-panel]')].find((candidate) => !candidate.hidden);
      const counter = this.querySelector('[data-shelf-counter]');
      const slider = panel?.querySelector('.scroll-slider__slider');
      const cards = panel ? [...panel.querySelectorAll('[data-shelf-card]')].filter((card) => !card.hidden) : [];
      if (!counter || !slider || !cards.length) return;

      const cardWidth = cards[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(slider).columnGap) || 0;
      const step = cardWidth + gap;
      const visibleCount = Math.max(1, Math.round((slider.clientWidth + gap) / step));
      const start = Math.min(cards.length - 1, Math.max(0, Math.round(Math.abs(slider.scrollLeft) / step)));
      counter.textContent = `${start + 1}–${Math.min(start + visibleCount, cards.length)} of ${cards.length}`;
    }
  }

  customElements.define('personalised-shelf-mock', PersonalisedShelfMock);
})();
